import { Input } from "../Authentication/Login/Input";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { useState, useMemo } from "react";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export const PasswordInput = ({
  value,
  onValueChange,
  autoComplete,
  showStrength = true,
  placeholder = "Enter your password...",
}: {
  value: string;
  onValueChange: (value: string) => void;
  autoComplete?: string;
  showStrength?: boolean;
  placeholder?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const validation = useMemo(
    () => ({
      length: value.length >= 12,
      letters: /[a-z]/.test(value),
      numbers: /[0-9]/.test(value),
      symbols: /[^A-Za-z0-9\s]/.test(value),
    }),
    [value],
  );

  const strength = useMemo(() => {
    return Object.values(validation).filter(Boolean).length;
  }, [validation]);

  const progressColor = useMemo(() => {
    if (strength === 1) return "bg-red-500";
    if (strength === 2) return "bg-orange-500";
    if (strength === 3) return "bg-yellow-500";
    if (strength === 4) return "bg-green-500";

    return "bg-gray-300";
  }, [strength]);

  return (
    <Popover open={showStrength && open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className="relative flex items-center"
          onClick={e => e.preventDefault()}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        >
          <Input
            type={isPasswordVisible ? "text" : "password"}
            placeholder={placeholder}
            autoComplete={autoComplete}
            value={value}
            onChange={e => onValueChange(e.target.value)}
          />
          <Button
            variant="ghost"
            type="button"
            className="absolute right-3 size-6 p-0"
            onClick={e => {
              e.stopPropagation();
              setIsPasswordVisible(!isPasswordVisible);
            }}
          >
            {isPasswordVisible ? (
              <EyeIcon className="size-5" />
            ) : (
              <EyeOffIcon className="size-5" />
            )}
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={e => e.preventDefault()}
        avoidCollisions
        side="top"
        align="center"
        sideOffset={8}
        className="bg-background/50 flex flex-col p-4 shadow-xl backdrop-blur"
      >
        <h5 className="text-center text-base font-bold">Password Strength</h5>
        <div className="mt-3 grid w-full grid-cols-4 gap-2">
          <div
            className={cn("h-1 w-full rounded-full bg-gray-300", {
              [progressColor]: strength >= 1,
            })}
          />
          <div
            className={cn("h-1 w-full rounded-full bg-gray-300", {
              [progressColor]: strength >= 2,
            })}
          />
          <div
            className={cn("h-1 w-full rounded-full bg-gray-300", {
              [progressColor]: strength >= 3,
            })}
          />
          <div
            className={cn("h-1 w-full rounded-full bg-gray-300", {
              [progressColor]: strength >= 4,
            })}
          />
        </div>
        <ul className="text-muted-foreground mt-4 list-disc pl-4 text-sm">
          <li
            className={cn({
              "text-emerald-500": validation.length,
            })}
          >
            At least 12 characters
          </li>
          <li
            className={cn({
              "text-emerald-500": validation.letters,
            })}
          >
            Contains letters
          </li>
          <li
            className={cn({
              "text-emerald-500": validation.numbers,
            })}
          >
            Contains numbers
          </li>
          <li
            className={cn({
              "text-emerald-500": validation.symbols,
            })}
          >
            Contains symbols (e.g. !@#$%^&*)
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  );
};
